import { Boxes, Pencil, Trash2, Upload, Video } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  ModelViewerCanvas,
  type ModelViewerHandle,
} from '@/components/canvas/elements/ModelViewerCanvas';
import { AppShell, Breadcrumbs } from '@/components/landing/AppShell';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { UploadProgressBar } from '@/components/ui/UploadProgressBar';
import { api } from '@/lib/api';
import type { Asset } from '@/lib/schema.types';
import { uploadFile } from '@/lib/uploads';

/**
 * The 3D model library — the first real "Assets" page. Upload, browse, tag, and
 * manage reusable models, and set each model's default ("home") camera pose so
 * every slide that reuses it loads framed. Models inserted onto slides are
 * picked from here (see BlockCreator's "From library" tab).
 */
type LibraryKind = 'model3d' | 'gif';

const KIND_COPY: Record<
  LibraryKind,
  { title: string; blurb: string; accept: string; button: string }
> = {
  model3d: {
    title: '3D Model Library',
    blurb:
      'Upload and manage reusable 3D models. Pick from here when adding a model to a slide.',
    accept: '.glb,.gltf,model/gltf-binary,model/gltf+json',
    button: 'Upload model',
  },
  gif: {
    title: 'Sticker Library',
    blurb:
      'Animated GIF/WebP accents — pins, arrows, paper planes. Pick from here in the Image block’s sticker strip.',
    accept: '.gif,.webp,image/gif,image/webp',
    button: 'Upload sticker',
  },
};

export default function AssetsPage() {
  const [kind, setKind] = useState<LibraryKind>('model3d');
  const [assets, setAssets] = useState<Asset[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileRef = useRef<HTMLInputElement>(null);

  const [poseFor, setPoseFor] = useState<Asset | null>(null);
  const [editFor, setEditFor] = useState<Asset | null>(null);

  const refresh = useCallback(
    async (q?: string) => {
      setLoading(true);
      setError(null);
      try {
        const list = await api.assets.list({ kind, search: q || undefined });
        setAssets(list);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to load library');
      } finally {
        setLoading(false);
      }
    },
    [kind],
  );

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const onUpload = async (file: File) => {
    const lower = file.name.toLowerCase();
    let fileFormat: 'glb' | 'gltf' | 'gif' | 'webp';
    let contentType: string;
    if (kind === 'gif') {
      if (!lower.endsWith('.gif') && !lower.endsWith('.webp')) {
        setError('Only .gif or .webp files are supported for stickers.');
        return;
      }
      fileFormat = lower.endsWith('.gif') ? 'gif' : 'webp';
      contentType = fileFormat === 'gif' ? 'image/gif' : 'image/webp';
    } else {
      if (!lower.endsWith('.glb') && !lower.endsWith('.gltf')) {
        setError('Only .glb or .gltf files are supported.');
        return;
      }
      fileFormat = lower.endsWith('.glb') ? 'glb' : 'gltf';
      contentType = fileFormat === 'glb' ? 'model/gltf-binary' : 'model/gltf+json';
    }
    setError(null);
    setUploading(true);
    setProgress(0);
    try {
      const uploaded = await uploadFile(file, {
        type: 'CONTENT',
        contentType,
        extension: fileFormat,
        onProgress: setProgress,
      });
      await api.assets.create({
        name: file.name.replace(/\.(glb|gltf|gif|webp)$/i, ''),
        kind,
        url: uploaded.url,
        s3Key: uploaded.key,
        contentType,
        fileFormat,
        fileSize: file.size,
        tags: [],
      });
      await refresh(search);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const onDelete = async (asset: Asset) => {
    if (!window.confirm(`Delete "${asset.name}" from the library? This cannot be undone.`)) return;
    try {
      await api.assets.remove(asset._id);
      setAssets((prev) => prev.filter((a) => a._id !== asset._id));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Delete failed');
    }
  };

  return (
    <AppShell activeNav="assets">
      <div className="mx-auto flex w-full max-w-[1100px] flex-col px-8 pt-6 pb-16">
        <Breadcrumbs
          items={[{ label: 'Workspace', href: '/lesson-packs' }, { label: 'Assets' }]}
        />

        <div className="mt-3 flex items-end justify-between gap-4">
          <div>
            <h1 className="text-[26px] font-semibold tracking-tight text-fg">
              {KIND_COPY[kind].title}
            </h1>
            <p className="mt-1 text-sm text-fg-muted">{KIND_COPY[kind].blurb}</p>
          </div>
          <div className="flex items-center gap-2">
            <input
              ref={fileRef}
              type="file"
              accept={KIND_COPY[kind].accept}
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void onUpload(f);
                e.target.value = '';
              }}
            />
            <Button
              variant="primary"
              disabled={uploading}
              onClick={() => fileRef.current?.click()}
            >
              <Upload size={16} />
              {uploading ? 'Uploading…' : KIND_COPY[kind].button}
            </Button>
          </div>
        </div>

        {/* Library kind tabs */}
        <div className="mt-5 inline-flex self-start rounded-lg border border-border bg-bg-subtle p-0.5">
          {(
            [
              { id: 'model3d', label: '3D Models' },
              { id: 'gif', label: 'Stickers (GIF)' },
            ] as { id: LibraryKind; label: string }[]
          ).map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => {
                setKind(t.id);
                setSearch('');
              }}
              className={
                kind === t.id
                  ? 'rounded-md bg-bg-panel px-3.5 py-1.5 text-[13px] font-medium text-fg shadow-sm'
                  : 'rounded-md px-3.5 py-1.5 text-[13px] font-medium text-fg-muted hover:text-fg'
              }
            >
              {t.label}
            </button>
          ))}
        </div>

        {uploading && (
          <div className="mt-4">
            <UploadProgressBar value={progress} />
          </div>
        )}

        <form
          className="mt-5 max-w-sm"
          onSubmit={(e) => {
            e.preventDefault();
            void refresh(search);
          }}
        >
          <Input
            placeholder="Search models by name or description…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </form>

        {error && (
          <p className="mt-4 rounded-md bg-red-50 px-3 py-2 text-xs text-red-600">{error}</p>
        )}

        {loading ? (
          <p className="mt-10 text-sm text-fg-muted">Loading library…</p>
        ) : assets.length === 0 ? (
          <div className="mt-10 flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-border bg-bg-panel/50 py-16 text-center">
            <span className="grid h-14 w-14 place-items-center rounded-xl bg-accent-soft text-accent">
              <Boxes size={24} />
            </span>
            <h3 className="mt-4 text-base font-semibold text-fg">No models yet</h3>
            <p className="mt-1.5 max-w-sm text-[13px] text-fg-muted">
              Upload a .glb or .gltf file to start building your reusable 3D library.
            </p>
          </div>
        ) : (
          <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {assets.map((asset) => (
              <AssetCard
                key={asset._id}
                asset={asset}
                onSetPose={() => setPoseFor(asset)}
                onEdit={() => setEditFor(asset)}
                onDelete={() => void onDelete(asset)}
              />
            ))}
          </div>
        )}
      </div>

      {poseFor && (
        <SetPoseModal
          asset={poseFor}
          onClose={() => setPoseFor(null)}
          onSaved={(updated) => {
            setAssets((prev) => prev.map((a) => (a._id === updated._id ? updated : a)));
            setPoseFor(null);
          }}
        />
      )}

      {editFor && (
        <EditAssetModal
          asset={editFor}
          onClose={() => setEditFor(null)}
          onSaved={(updated) => {
            setAssets((prev) => prev.map((a) => (a._id === updated._id ? updated : a)));
            setEditFor(null);
          }}
        />
      )}
    </AppShell>
  );
}

function AssetCard({
  asset,
  onSetPose,
  onEdit,
  onDelete,
}: {
  asset: Asset;
  onSetPose: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <div className="group flex flex-col overflow-hidden rounded-xl border border-border bg-bg-panel">
      <div className="relative h-40 bg-gradient-to-br from-slate-100 to-slate-200">
        {asset.kind === 'gif' ? (
          <img src={asset.url} alt={asset.name} className="h-full w-full object-contain" />
        ) : asset.poster ? (
          <img src={asset.poster} alt={asset.name} className="h-full w-full object-cover" />
        ) : (
          <ModelViewerCanvas
            src={asset.url}
            poster={asset.poster ?? undefined}
            alt={asset.name}
            autoRotate
            pose={
              asset.defaultCameraOrbit
                ? {
                    cameraOrbit: asset.defaultCameraOrbit,
                    cameraTarget: asset.defaultCameraTarget ?? undefined,
                  }
                : null
            }
            className="h-full w-full"
            style={{ width: '100%', height: '100%', backgroundColor: 'transparent' }}
          />
        )}
        <span className="absolute left-2 top-2 rounded bg-slate-900/70 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
          {asset.fileFormat}
        </span>
      </div>
      <div className="flex flex-1 flex-col gap-1.5 p-3">
        <p className="truncate text-sm font-medium text-fg" title={asset.name}>
          {asset.name}
        </p>
        {asset.tags.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {asset.tags.slice(0, 3).map((t) => (
              <span key={t} className="rounded bg-bg-subtle px-1.5 py-0.5 text-[10px] text-fg-muted">
                {t}
              </span>
            ))}
          </div>
        )}
        <div className="mt-auto flex items-center gap-1 pt-1">
          {asset.kind === 'model3d' && (
            <Button size="sm" variant="ghost" onClick={onSetPose} title="Set default camera pose">
              <Video size={14} /> Pose
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={onEdit} title="Rename / tags">
            <Pencil size={14} />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={onDelete}
            title="Delete"
            className="text-red-500 hover:text-red-600"
          >
            <Trash2 size={14} />
          </Button>
        </div>
      </div>
    </div>
  );
}

function SetPoseModal({
  asset,
  onClose,
  onSaved,
}: {
  asset: Asset;
  onClose: () => void;
  onSaved: (updated: Asset) => void;
}) {
  const viewerRef = useRef<ModelViewerHandle>(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = async () => {
    const pose = viewerRef.current?.capturePose();
    if (!pose) {
      setErr('Could not read the camera — wait for the model to load, then try again.');
      return;
    }
    setSaving(true);
    setErr(null);
    try {
      const updated = await api.assets.update(asset._id, {
        defaultCameraOrbit: pose.cameraOrbit,
        defaultCameraTarget: pose.cameraTarget,
      });
      onSaved(updated);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to save pose');
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Set default camera pose"
      description="Orbit the model to the view you want it to load at, then capture it."
      width="lg"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => void save()} disabled={saving}>
            {saving ? 'Saving…' : 'Capture this view'}
          </Button>
        </div>
      }
    >
      <div className="h-80 w-full overflow-hidden rounded-lg bg-gradient-to-br from-slate-100 to-slate-200">
        <ModelViewerCanvas
          ref={viewerRef}
          src={asset.url}
          poster={asset.poster ?? undefined}
          alt={asset.name}
          cameraControls
          pose={
            asset.defaultCameraOrbit
              ? {
                  cameraOrbit: asset.defaultCameraOrbit,
                  cameraTarget: asset.defaultCameraTarget ?? undefined,
                }
              : null
          }
          className="h-full w-full"
          style={{ width: '100%', height: '100%', backgroundColor: 'transparent' }}
        />
      </div>
      {err && <p className="mt-3 text-xs text-red-600">{err}</p>}
    </Modal>
  );
}

function EditAssetModal({
  asset,
  onClose,
  onSaved,
}: {
  asset: Asset;
  onClose: () => void;
  onSaved: (updated: Asset) => void;
}) {
  const [name, setName] = useState(asset.name);
  const [tags, setTags] = useState(asset.tags.join(', '));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = async () => {
    setSaving(true);
    setErr(null);
    try {
      const updated = await api.assets.update(asset._id, {
        name: name.trim() || asset.name,
        tags: tags
          .split(',')
          .map((t) => t.trim())
          .filter(Boolean),
      });
      onSaved(updated);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to save');
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Edit model"
      width="sm"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => void save()} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      }
    >
      <div className="space-y-3">
        <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        <Input
          label="Tags (comma-separated)"
          value={tags}
          onChange={(e) => setTags(e.target.value)}
          placeholder="heart, anatomy, organ"
        />
        {err && <p className="text-xs text-red-600">{err}</p>}
      </div>
    </Modal>
  );
}
